import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { InputDialogComponent } from '@cube/lib-common';
import { ThemeService } from '../../services/theme/theme.service';
import { Store } from '@ngrx/store';
import { AppState, User } from 'src/app/store/states/auth.state';
import { Observable } from 'rxjs';
import { filter, tap } from 'rxjs/operators';
import { selectUserDetails } from 'src/app/store/selectors/auth.selectors';
import { SystemActions } from 'src/app/store/action-types/system.action-types';
import { AuthActions } from 'src/app/store/action-types/auth.action-types';

@Component({
  selector: 'cube-user-menu',
  templateUrl: './user-menu.component.html',
  styleUrls: ['./user-menu.component.scss'],
})
export class UserMenuComponent implements OnInit {
  @Output() onDepartmentClick: EventEmitter<any> = new EventEmitter<any>();
  @Input() user: User = null;

  isOpen: boolean = false;
  activeRole: string;
  activeTenant: string;

  user$: Observable<User | undefined>;
  userTenantList: Array<string> = [];
  userRoleList: Array<string> = [];

  private theme: boolean;

  constructor(private readonly store: Store<AppState>, private elementRef: ElementRef, public dialog: MatDialog, private themeService: ThemeService) {}

  @HostListener('document:click', ['$event', '$event.target'])
  onClick(event: MouseEvent, targetElement: HTMLElement): void {
    if (!targetElement) {
      return;
    }

    const clickedInside = this.elementRef.nativeElement.contains(targetElement);
    if (!clickedInside) {
      this.isOpen = false;
    }
  }

  ngOnInit(): void {
    this.user$ = this.store.select(selectUserDetails).pipe(
      filter((user: User): boolean => !!user),
      tap((user) => {
        this.userTenantList = user.tenants.map((tenant) => tenant.name);
        this.userRoleList = user.roles.map((role) => role.name);
        this.activeTenant = user?.active_tenant?.name || user.tenants[0].name;
        this.activeRole = user?.active_role?.name || user.roles[0].name;
      })
    );

    this.themeService.isDarkTheme.subscribe((theme) => (this.theme = theme));
  }

  changeTenant(user: User): void {
    const dialogRef = this.dialog.open(InputDialogComponent, {
      width: '400px',
      height: '200px',
      data: {
        title: 'Tenant selection',
        confirmLabel: 'Ok',
        cancelLabel: 'Cancel',
        inputs: [
          {
            type: 'select',
            label: 'Select tenant',
            required: true,
            options: this.userTenantList,
            value: this.activeTenant,
            fieldDisplayed: '',
          },
        ],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.data) {
        const active_tenant = user.tenants.find((tenant) => tenant.name === result.data.inputs[0].value)!;
        this.store.dispatch(SystemActions.RedirectToHomePage());
        this.store.dispatch(AuthActions.SetActiveTenant({ active_tenant }));
      }
    });
  }

  changeRole(user) {
    const dialogRef = this.dialog.open(InputDialogComponent, {
      width: '400px',
      height: '200px',
      data: {
        title: 'Role selection',
        confirmLabel: 'Ok',
        cancelLabel: 'Cancel',
        inputs: [
          {
            type: 'select',
            label: 'Select role',
            required: true,
            options: this.userRoleList,
            value: this.activeRole,
            fieldDisplayed: '',
          },
        ],
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.data) {
        const active_role = user.roles.find((role) => role.name === result.data.inputs[0].value)!;
        this.store.dispatch(SystemActions.RedirectToHomePage());
        this.store.dispatch(AuthActions.SetActiveRole({ active_role }));
      }
    });
  }

  public openSupportPage(): void {
    window.open('https://support.cube.rina.org', '_blank');
  }

  changeTheme(): void {
    this.themeService.setDarkTheme(!this.theme);
  }

  doLogout(): void {
    this.store.dispatch(AuthActions.Logout());
  }
}
